// src/encounter/structured-note/objective-vitals-model.ts
//
// D-74 (design desk, 6 Oct 2026) · frame v2 only. The pure rules behind the Vitals block in Objective
// (objective-vitals.tsx draws it): where the one block goes, and what a provider signature takes away.
import type { AuthorRole, QItem, Questionnaire, QuestionnaireRendererProps, VitalsBlockProps } from '@scripsteam/scrips-signal-ds';
import type { PaStepKey } from '../lib/encounter-steps';
import { questionnaireForStep } from '../lib/questionnaire-groups';
import { vitalsMarkers } from './note-adapter';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** The visit's vitals read, as the page knows it (D-45 states). Absent → `ready`. */
export interface VitalsReadState {
  status: 'ready' | 'loading' | 'failed' | 'unavailable';
  /** Re-reads the vitals; offered only on `failed`, and only with a handler. */
  onRetry?: () => void;
  /** The latest reading's date (ISO, from the vitals the page already loaded); absent with no reading. */
  latestAt?: string;
}

/** "Latest office vitals 10 Aug 2026" for the block's head, or nothing with no readable date. */
export function latestVitalsLabel(latestAt: string | undefined): string | undefined {
  if (!latestAt) return undefined;
  const d = new Date(latestAt);
  if (Number.isNaN(d.getTime())) return undefined;
  return `Latest office vitals ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export type ResolveVitals = NonNullable<QuestionnaireRendererProps['resolveVitals']>;

/** The block's title. With the frame-drawn block the pills header carries no second title (L9). */
export const OBJECTIVE_VITALS_TITLE = 'Vitals';

/** The marker handed to the page's resolver for the frame-drawn block (the resolver reads no field of it). */
export const OBJECTIVE_VITALS_MARKER: QItem = { linkId: 'frame-objective-vitals', type: 'group', itemControl: 'vitals-block' } as QItem;

/** Does the template carry a `vitals-block` marker inside Objective (the PE step the note draws)? */
export function objectiveHasVitalsMarker(questionnaire: Questionnaire | undefined, navigableSteps: readonly PaStepKey[]): boolean {
  if (!questionnaire || !navigableSteps.includes('pe')) return false;
  return vitalsMarkers(questionnaireForStep(questionnaire, 'pe').item).length > 0;
}

/**
 * Does the frame draw the Vitals block first in Objective? Only for the Objective section, only for the
 * physician and the nurse, and only when Objective carries no `vitals-block` marker (then the block draws at
 * that marker and only there — exactly one block, L5). With no resolver the block still draws, in D-45's
 * "unavailable" state — unknown stays unknown, never an absent block (Codex P2, 6 Oct).
 */
export function drawsObjectiveVitals(args: {
  sectionId: string;
  author: AuthorRole;
  questionnaire?: Questionnaire;
  navigableSteps: readonly PaStepKey[];
}): boolean {
  if (args.sectionId !== 'o') return false;
  if (args.author !== 'physician' && args.author !== 'nurse') return false;
  return !objectiveHasVitalsMarker(args.questionnaire, args.navigableSteps);
}

/** A provider signature freezes every write the block offers (rule 38); Historical stays. */
export function frozenVitals(props: VitalsBlockProps, frozen: boolean): VitalsBlockProps {
  return frozen ? { ...props, onEdit: undefined, onVitalsReviewed: undefined, onItemClick: undefined } : props;
}

/** `?x=physician-vitals-readonly` (dev default, Antonin 3 Nov): the physician reads the nurse's vitals in the PE block, with no
 *  Edit / Add. History and Mark reviewed stay. Every other role keeps Edit. */
export function physicianReadsVitalsOnly(clinicalRole: string): boolean {
  return clinicalRole === 'physician' && isEncounterFlagOn('physicianVitalsReadonly');
}

/** The PE vitals block's actions (`resolveVitals`). Every action needs the ids (`editable`); Mark reviewed also needs a reading.
 *  `readOnly` (physicianReadsVitalsOnly) drops Edit and its "Add" label; History and Mark reviewed stay. */
export function vitalsBlockActions(args: {
  editable: boolean;
  hasReadings: boolean;
  frameV2On: boolean;
  readOnly: boolean;
  onReviewed: () => void;
  onHistorical: () => void;
  onEdit: () => void;
}): Pick<VitalsBlockProps, 'onVitalsReviewed' | 'onHistorical' | 'onEdit'> & { editLabel?: string } {
  const { editable, hasReadings, frameV2On, readOnly } = args;
  return {
    onVitalsReviewed: editable && hasReadings ? args.onReviewed : undefined,
    onHistorical: editable ? args.onHistorical : undefined,
    onEdit: editable && !readOnly ? args.onEdit : undefined,
    // D-74: frame v2 with no readings labels the action "Add"; read-only has no action to label.
    ...(readOnly ? {} : vitalsActionLabel(frameV2On, hasReadings)),
  };
}

/**
 * D-74 · frame v2 only: with no readings for the visit the header's action is "Add" (the same Edit dialog
 * and save path); with readings, or with the switch OFF, the resolver is unchanged (the DS default "Edit").
 */
export function vitalsActionLabel(frameV2On: boolean, hasReadings: boolean): { editLabel?: string } {
  return frameV2On && !hasReadings ? { editLabel: 'Add' } : {};
}

/** D-45 · the page's vitals read → the block's state: not read yet (no visit ids) is "unavailable". */
export function vitalsReadState(args: { idsResolved: boolean; isError: boolean; isPending: boolean; refetch: () => unknown; latestAt?: string }): VitalsReadState {
  if (!args.idsResolved) return { status: 'unavailable' };
  if (args.isError) return { status: 'failed', onRetry: () => void args.refetch() };
  if (args.isPending) return { status: 'loading' };
  return { status: 'ready', ...(args.latestAt ? { latestAt: args.latestAt } : {}) };
}
